/**
 * Domain types for the Lists feature.
 * All server-side snake_case fields are mapped to camelCase here.
 */

/** Distinguishes between free-text items and catalog-linked items */
export enum ListItemType {
    FREE = 'free',
    LINKED = 'linked',
}

/** Lifecycle states of a list execution */
export type ListExecutionStatus =
    | 'resolving'
    | 'draft'
    | 'confirmed'
    | 'sent_to_cart'
    | 'completed'
    | 'cancelled';

/**
 * A single product candidate returned by the AI resolver for a free-text list item.
 */
export interface CandidateProduct {
    id: string;
    catalogProductId: number;
    name: string;
    brand: string;
    imageUrl: string | null;
    price: number | null;
    unit: string | null;
    confidenceScore: number;
    rank: number;
    selected: boolean;
    /** Hashed SKU from the API - may be null if not yet resolved */
    sku?: string | null;
}

/**
 * Represents one item inside a shopping list.
 * - `type === FREE`   → rawDescription is a user-typed description; catalogProductId is null.
 * - `type === LINKED` → catalogProductId references a known SKU in the catalog.
 */
export interface ListItem {
    id: string;
    listId: string;
    rawDescription: string;
    catalogProductId: string | null;
    linkedProductName: string | null;
    catalogProductSku: string | null;
    itemImgUrl: string | null;
    categoryId: number | null;
    quantity: number;
    unit: string | null;
    notes: string | null;
    sortOrder: number;
    type: ListItemType;
}

/**
 * A resolved list item with its candidate fields merged inline.
 * Replaces the nested ItemWithCandidates structure.
 */
export interface ResolvedListItem extends ListItem {
    /** Catalog product name from selected candidate (falls back to rawDescription). */
    name: string | null;
    brand: string | null;
    imageUrl: string | null;
    price: number | null;
    sku: string | null;
    confidenceScore: number | null;
    selectedCandidateId: string | null;
}

/** Minimal item data used for preview thumbnails in list cards */
export interface SampleListItem {
    itemImgUrl: string | null;
}

/** Compact representation used in list cards and collection views */
export interface List {
    id: string;
    name: string;
    description: string | null;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    itemCount: number;
    sampleItems: SampleListItem[];
}

/** Full list including all its items — used for the detail panel */
export interface ListDetail extends List {
    items: ListItem[];
}

/**
 * A temporary item with resolved candidates from the backend.
 * Used for free-text items sent in the execute payload.
 */
export interface TemporaryExecutionItem {
    tempKey: string;
    rawDescription: string;
    quantity: number;
    candidates: CandidateProduct[];
}

/**
 * Represents an execution instance of a shopping list.
 * Created when the user taps "Ejecutar Lista".
 */
export interface ListExecution {
    id: string;
    listId: string;
    status: ListExecutionStatus;
    itemsWithCandidates: ResolvedListItem[];
    temporaryItemsWithCandidates: ResolvedListItem[];
    cartId: string | null;
}

/** Summary view of a past execution — used in execution history */
export interface ListExecutionSummary {
    id: string;
    listId: string;
    status: ListExecutionStatus;
    createdAt: string;
    cartId: string | null;
}

/** Payload to create or update a list item */
export interface CreateListItemPayload {
    rawDescription: string;
    catalogProductId?: string | null;
    /** hashed_sku from the catalog — used when the catalog id is unreliable (e.g. GCR-generated). Backend resolves to the real products.id. */
    catalogProductSku?: string | null;
    categoryId?: number | null;
    quantity: number;
    unit?: string | null;
    notes?: string | null;
    /** Display name of the selected catalog product. Passed to parent for temporary quote items. */
    name?: string | null;
}

/**
 * A product added temporarily in view mode for quote purposes only.
 * Not persisted to the list — discarded when navigating away.
 *
 * When catalogProductId is null, the item is a free-text entry and rawDescription
 * carries the user's typed input. Linked items (catalogProductId set) behave as before.
 */
export interface TemporaryQuoteItem {
    /** Unique key for React rendering (crypto.randomUUID). */
    key: string;
    /** Catalog product ID when linked to a known product; null for free-text items. */
    catalogProductId: string | null;
    sku: string | null;
    name: string;
    categoryId: number;
    imageUrl: string | null;
    quantity: number;
    /** User's raw free-text description — set only when catalogProductId is null. */
    rawDescription?: string;
}

/**
 * A product candidate returned by the item search endpoint.
 * Mirrors the backend `ItemSearchResult` schema.
 */
export interface ItemSearchResult {
    id: string;
    s_id: number;
    name: string;
    brand: string;
    imageUrl: string | null;
    price: number | null;
    unit: string | null;
    total: number | null;
    categoryId: number;
    parsedQuantity: number;
    parsedUnit: string | null;
    /** hashed_sku — used by getPrice on add-to-cart. */
    sku: string | null;
}
